import axios from 'axios';
import { Auth, Hub } from 'aws-amplify';
import * as Sentry from '@sentry/browser';

const MAX_RETRIES = 3;

const epaxios = axios.create({
  baseURL: `${import.meta.env.VITE_BASE_URL}/v1/`,
});

async function getSessionToken() {
  const session = await Auth.currentSession();
  return session.getIdToken().getJwtToken();
}

async function handleSignOut(error) {
  try {
    await Auth.signOut();
  } catch (signOutError) {
    Sentry.captureException(signOutError);
  } finally {
    Hub.dispatch('auth', { event: 'signOut' });
  }
  return Promise.reject(error);
}

function withRetry(fn, retryCount = 0) {
  return async (...args) => {
    try {
      return await fn(...args);
    } catch (error) {
      if (retryCount < MAX_RETRIES) {
        const backoffDelay = Math.pow(2, retryCount) * 1000;
        await new Promise(resolve => setTimeout(resolve, backoffDelay));
        return withRetry(fn, retryCount + 1)(...args);
      }
      throw error;
    }
  };
}

epaxios.interceptors.request.use(
  async config => {
    try {
      const token = await withRetry(getSessionToken)();
      config.headers.Authorization = `Bearer ${token}`;
      return config;
    } catch (error) {
      return handleSignOut(error);
    }
  },
  error => {
    Sentry.captureException(error);
    return Promise.reject(error);
  },
);

epaxios.interceptors.response.use(
  response => response,
  async error => {
    // Handle 401 errors
    if (error.response?.status === 401) {
      try {
        const token = await withRetry(getSessionToken)();
        error.config.headers.Authorization = `Bearer ${token}`;
        return epaxios(error.config);
      } catch (refreshError) {
        return handleSignOut(refreshError);
      }
    }

    // For non-401 errors, simply reject
    Sentry.captureException(error);
    return Promise.reject(error);
  },
);

export default epaxios;
